import { useState, useEffect } from 'react';
import { Mail, Plus, X, Upload, FolderOpen } from 'lucide-react';
// [DISABLED] Expiration & OTP — décommentez les imports ci-dessous pour réactiver
// import { Calendar, Shield } from 'lucide-react';
// import { Switch } from '@/components/ui/switch';
import { cn } from '@/lib/utils/utils';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { FolderTreeSelector } from '@/components/ui/app/folder-tree-selector';
import DialogImportEmails from '@/pages/procedures/index/_partials/dialogs/DialogImportEmails';
import { useTranslation } from 'react-i18next';

export const FORM_INVITATION_CREATE_ID = 'form-invitation-create';

/**
 * Formulaire de création d'invitations (multi-emails)
 */
export default function FormInvitationCreate({ onSubmit, onCanSubmitChange }) {
    const { t } = useTranslation();
    const [emailInput, setEmailInput] = useState('');
    const [emails, setEmails] = useState([]);
    const [isImportDialogOpen, setIsImportDialogOpen] = useState(false);

    // Folder selection
    const [folderMode, setFolderMode] = useState('new'); // 'new' | 'existing'
    const [newFolderName, setNewFolderName] = useState('');
    const [selectedTreeNode, setSelectedTreeNode] = useState(null); // { type, uniq_id, path, name, folderUniqId }

    // [DISABLED] Expiration & OTP — décommentez pour réactiver
    // const [expiresInDays, setExpiresInDays] = useState(7);
    // const [isOtpRequired, setIsOtpRequired] = useState(true);

    // Validation selon le mode de dossier
    const isFolderValid = folderMode === 'new' ? newFolderName.trim().length > 0 : !!selectedTreeNode;
    // [DISABLED] Expiration — décommentez pour réactiver la validation expiration
    // const canSubmit = emails.length > 0 && isFolderValid && expiresInDays > 0;
    const canSubmit = emails.length > 0 && isFolderValid;

    useEffect(() => {
        onCanSubmitChange?.(canSubmit);
    }, [canSubmit, onCanSubmitChange]);

    const isValidEmail = (email) => {
        return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
    };

    const addEmail = () => {
        const trimmedEmail = emailInput.trim().toLowerCase();
        if (trimmedEmail && isValidEmail(trimmedEmail) && !emails.includes(trimmedEmail)) {
            setEmails([...emails, trimmedEmail]);
            setEmailInput('');
        }
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            addEmail();
        }
    };

    const removeEmail = (emailToRemove) => {
        setEmails(emails.filter((email) => email !== emailToRemove));
    };

    const handleImportEmails = (importedEmails) => {
        const newEmails = importedEmails
            .map((e) => e.trim().toLowerCase())
            .filter((email) => email && isValidEmail(email) && !emails.includes(email));

        setEmails([...emails, ...newEmails]);
        setIsImportDialogOpen(false);
    };

    const handleSubmit = (e) => {
        e.preventDefault();

        // Déterminer folder_id et parent_subfolder_id selon la sélection
        let folderId = null;
        let parentSubfolderId = null;

        if (folderMode === 'existing' && selectedTreeNode) {
            if (selectedTreeNode.type === 'folder') {
                // Sélection d'un dossier racine
                folderId = selectedTreeNode.uniq_id;
            } else if (selectedTreeNode.type === 'subfolder') {
                // Sélection d'un sous-dossier
                folderId = selectedTreeNode.folderUniqId;
                parentSubfolderId = selectedTreeNode.uniq_id;
            }
        }

        onSubmit({
            emails,
            folder_mode: folderMode,
            folder_id: folderId,
            folder_name: folderMode === 'new' ? newFolderName.trim() : null,
            parent_subfolder_id: parentSubfolderId,
            // [DISABLED] Expiration & OTP — décommentez pour réactiver
            // expires_in_days: expiresInDays,
            // is_otp_required: isOtpRequired,
            is_otp_required: true,
        });
    };

    const resetForm = () => {
        setEmails([]);
        setEmailInput('');
        setFolderMode('new');
        setNewFolderName('');
        setSelectedTreeNode(null);
        // [DISABLED] Expiration & OTP — décommentez pour réactiver
        // setExpiresInDays(7);
        // setIsOtpRequired(true);
    };

    return (
        <>
            <form id={FORM_INVITATION_CREATE_ID} onSubmit={handleSubmit} className="space-y-[20px]">
                {/* Destinataires */}
                <div>
                    <h3 className="text-[15px] text-foreground mb-[12px]">
                        <Mail className="inline h-4 w-4 mr-[6px] text-muted-foreground" />
                        {t('procedures:destinataires')}
                        <span className="text-destructive ml-1">*</span>
                    </h3>

                    <div className="bg-muted rounded-[12px] p-[14px]">
                        {/* Email Input */}
                        <div className="flex gap-[8px] mb-[12px]">
                            <Input
                                type="email"
                                placeholder={t('procedures:ajouterUneAdresseEmail')}
                                value={emailInput}
                                onChange={(e) => setEmailInput(e.target.value)}
                                onKeyDown={handleKeyDown}
                                className="flex-1 bg-card rounded-[8px] px-[12px] h-[40px] text-[13px]"
                            />
                            <Button
                                type="button"
                                onClick={addEmail}
                                disabled={!emailInput.trim() || !isValidEmail(emailInput.trim())}
                                variant="solid"
                                color="secondary"
                                className="rounded-[8px] h-[40px] w-[40px] p-0 disabled:opacity-50"
                            >
                                <Plus className="h-5 w-5" strokeWidth={2.5} />
                            </Button>
                        </div>

                        {/* Import Button */}
                        <Button
                            type="button"
                            variant="plain"
                            onClick={() => setIsImportDialogOpen(true)}
                            className="w-full bg-card rounded-[10px] py-[10px] mb-[12px] hover:bg-border h-auto flex items-center justify-center gap-[6px] border border-border"
                        >
                            <Upload className="h-4 w-4 text-foreground" />
                            <span className="text-[13px] text-foreground">{t('procedures:importerUneListeDEmails')}</span>
                        </Button>

                        {/* Email List */}
                        {emails.length > 0 && (
                            <div className="space-y-[6px] max-h-[200px] overflow-y-auto">
                                {emails.map((email) => (
                                    <div
                                        key={email}
                                        className="flex items-center justify-between bg-card rounded-[8px] px-[12px] py-[8px] border border-border"
                                    >
                                        <div className="flex items-center gap-[8px] flex-1 min-w-0">
                                            <Mail className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                                            <span className="text-[12px] text-foreground truncate">{email}</span>
                                        </div>
                                        <Button
                                            type="button"
                                            variant="plain"
                                            size="icon"
                                            onClick={() => removeEmail(email)}
                                            className="text-destructive hover:text-destructive/70 hover:bg-transparent ml-[8px] h-auto w-auto p-0"
                                        >
                                            <X className="h-4 w-4" />
                                        </Button>
                                    </div>
                                ))}
                            </div>
                        )}

                        {/* Count */}
                        {emails.length > 0 && (
                            <p className="text-[12px] text-muted-foreground mt-[8px] text-center">
                                {emails.length} destinataire{emails.length > 1 ? 's' : ''}
                            </p>
                        )}
                    </div>
                </div>

                {/* Folder Selector */}
                <div>
                    <h3 className="text-[15px] text-foreground mb-[12px]">
                        <FolderOpen className="inline h-4 w-4 mr-[6px] text-muted-foreground" />
                        {t('procedures:dossierDeDestination')}
                        <span className="text-destructive ml-1">*</span>
                    </h3>
                    <div className="bg-muted rounded-[12px] p-[14px]">
                        {/* Tabs */}
                        <div className="bg-border rounded-[10px] p-[4px] flex items-center gap-[4px] mb-[14px]">
                            <Button
                                type="button"
                                variant="plain"
                                onClick={() => setFolderMode('new')}
                                className={cn(
                                    'flex-1 py-[8px] rounded-[8px] text-[13px] transition-colors h-auto',
                                    folderMode === 'new'
                                        ? 'bg-card text-foreground shadow-sm hover:bg-card hover:text-foreground'
                                        : 'bg-transparent text-muted-foreground hover:text-foreground hover:bg-transparent',
                                )}
                            >
                                {t('procedures:nouveauDossier')}
                            </Button>
                            <Button
                                type="button"
                                variant="plain"
                                onClick={() => setFolderMode('existing')}
                                className={cn(
                                    'flex-1 py-[8px] rounded-[8px] text-[13px] transition-colors h-auto',
                                    folderMode === 'existing'
                                        ? 'bg-card text-foreground shadow-sm hover:bg-card hover:text-foreground'
                                        : 'bg-transparent text-muted-foreground hover:text-foreground hover:bg-transparent',
                                )}
                            >
                                {t('procedures:dossierExistant')}
                            </Button>
                        </div>

                        {/* Content based on mode */}
                        {folderMode === 'new' ? (
                            <div>
                                <Input
                                    type="text"
                                    placeholder={t('procedures:nomDuNouveauDossier')}
                                    value={newFolderName}
                                    onChange={(e) => setNewFolderName(e.target.value)}
                                    className="w-full bg-card border border-border rounded-[8px] px-[12px] h-[40px] text-[13px]"
                                />
                                <p className="text-[11px] text-muted-foreground mt-[8px]">
                                    {t('procedures:unNouveauDossierSeraCreePourChaqueInvitation')}
                                </p>
                            </div>
                        ) : (
                            <div>
                                <FolderTreeSelector
                                    value={selectedTreeNode?.uniq_id}
                                    onChange={setSelectedTreeNode}
                                    excludeProcedures={true}
                                    scrollHeight="h-[200px]"
                                />
                                {selectedTreeNode && (
                                    <p className="text-[11px] text-muted-foreground mt-[8px]">
                                        {t('procedures:destination')} {selectedTreeNode.path}
                                    </p>
                                )}
                                {!selectedTreeNode && (
                                    <p className="text-[11px] text-muted-foreground mt-[8px]">
                                        {t('procedures:selectionnezUnDossierOuSousDossier')}
                                    </p>
                                )}
                            </div>
                        )}
                    </div>
                </div>

                {/* [DISABLED] Expiration & OTP — décommentez les blocs ci-dessous pour réactiver */}
                {/* Expiration */}
                {/*
                <div>
                    <h3 className="text-[15px] text-foreground mb-[12px]">
                        <Calendar className="inline h-4 w-4 mr-[6px] text-muted-foreground" />
                        Durée de validité
                    </h3>
                    <div className="bg-muted rounded-[12px] p-[14px]">
                        <div className="flex items-center gap-[12px]">
                            <Input
                                type="number"
                                min={1}
                                max={30}
                                value={expiresInDays}
                                onChange={(e) =>
                                    setExpiresInDays(Math.max(1, Math.min(30, parseInt(e.target.value) || 1)))
                                }
                                className="w-[80px] bg-card border border-border rounded-[8px] px-[12px] h-[40px] text-[13px] text-center"
                            />
                            <span className="text-[13px] text-muted-foreground">jours</span>
                        </div>
                        <p className="text-[11px] text-muted-foreground mt-[8px]">
                            Après cette durée, les invitations expireront automatiquement
                        </p>
                    </div>
                </div>
                */}

                {/* OTP Toggle */}
                {/*
                <div>
                    <h3 className="text-[15px] text-foreground mb-[12px]">
                        <Shield className="inline h-4 w-4 mr-[6px] text-muted-foreground" />
                        Sécurité
                    </h3>
                    <div className="bg-muted rounded-[12px] p-[14px]">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-[13px] text-foreground">Vérification par code OTP</p>
                                <p className="text-[11px] text-muted-foreground mt-[2px]">
                                    Un code sera envoyé par email
                                </p>
                            </div>
                            <Switch
                                checked={isOtpRequired}
                                onCheckedChange={setIsOtpRequired}
                                className="data-[state=checked]:bg-secondary"
                            />
                        </div>
                    </div>
                </div>
                */}
            </form>

            {/* Import Dialog */}
            <DialogImportEmails
                open={isImportDialogOpen}
                onClose={() => setIsImportDialogOpen(false)}
                onImport={handleImportEmails}
            />
        </>
    );
}

// Export pour permettre le reset depuis l'extérieur via ref
FormInvitationCreate.formId = FORM_INVITATION_CREATE_ID;
